import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoggerService } from './logger.service';
import { PageCacheService } from './page-cache.service';
import { PublicapiV1Service } from './publicapi.v1.service';

@Injectable({
  providedIn: 'root'
})
export class GalleryService {

  documentChangedSinceLastDownload = false;
  unsavedChangesTooltipClosedAt = new Date('2000-01-01');

  constructor(private http: HttpClient, private logger: LoggerService, private pageCache: PageCacheService, private apiV1: PublicapiV1Service) { }
  list(document) {
    if (document === undefined) document = "";
    var promise = new Promise((resolve, reject) => {
      this.http.get("api/gallery/list?Document=" + document).subscribe({
        next: (data: any) => resolve(data),
        error: (err: Error) => reject(this.logger.logApiError(err, "list"))
      });
    });
    return promise;
  }

  deleteDocument(document) {
    var promise = new Promise((resolve, reject) => {
      this.http.delete("api/gallery/document/" + document).subscribe({
        next: (data: any) => resolve(data),
        error: (err: Error) => reject(this.logger.logApiError(err, "deleteDocument"))
      });
    });
    return promise;
  }

   upload(fileData, fileName, fileType, appendAfterIndex) {
     this.pageCache.clear();

     var promise = new Promise((resolve, reject) => {
       this.http.post("api/upload?FileType=" + encodeURIComponent(fileType) + "&DocumentName=" + encodeURIComponent(fileName) + "&AppendAfterIndex=" + appendAfterIndex, fileData).subscribe({
         next: (data: any) => 
           resolve(data),
         error: (err: Error) => reject(this.logger.logApiError(err, "upload"))
       });
     });

     return promise;
   }

  fetchGDriveFile(file, token) {
    this.pageCache.clear();
    var promise = new Promise((resolve, reject) => {
      this.http.post("api/googledrive/fetchfile", { Id: file.id, Name: file.name, Token: token, Bytes: file.sizeBytes }).subscribe({
        next: (data: any) => resolve(data),
        error: (err: Error) => reject(this.logger.logApiError(err, "getGDriveFile"))
      });
    });
    return promise;
  }

  fetchDropboxFile(f) {
    this.pageCache.clear();
    var promise = new Promise((resolve, reject) => {
      this.http.post("api/dropbox/fetchfile", f).subscribe({
        next: (data: any) => resolve(data),
        error: (err: Error) => reject(this.logger.logApiError(err, "fetchDropboxFile"))
      });
    });
    return promise;
  }

  movePage(document, from, to) {
    var promise = new Promise((resolve, reject) => {
      this.http.post("api/gallery/movepage", { Document: document, From: from, To: to }).subscribe({
        next: (data: any) => resolve(data),
        error: (err: Error) => reject(this.logger.logApiError(err, "movePage"))
      });
    });
    return promise;
  }

  deletePage(document, page, pNumber = '0') {
    var promise = new Promise((resolve, reject) => {
      this.http.delete("api/gallery/page?Document=" + document + "&Page=" + page + "&Pnumber=" + pNumber).subscribe({
        next: (data: any) => resolve(data),
        error: (err: Error) => reject(this.logger.logApiError(err, "deletePage"))
      });
    });
    return promise;
  }

  getPage(document, page, token) {
    if (this.pageCache.contains(page)) {
      return new Promise(resolve => {
        var data = this.pageCache.get(page);
        data.documentModified = this.pageCache.getDocumentModified();
        resolve(data);
      });
    }

    if (token) {
      var promise = new Promise((resolve, reject) => {
        this.http.get("api/gallery/page?Document=" + document + "&Page=" + page + "&Token=" + token).subscribe(
          {
            next: (data: any) => {
              this.pageCache.add(page, data);
              this.pageCache.setDocumentModified(data.documentModified);
              resolve(data)
            },
            error: (err: Error) => this.logger.logApiError(err, "getPage")
          }
        );
      });
      return promise;
    } else {
      var promise = new Promise((resolve, reject) => {
        this.http.get("api/gallery/page?Document=" + document + "&Page=" + page).subscribe({
          next: (data: any) => {
            this.pageCache.add(page, data);
            resolve(data);
          },
          error: (err: Error) => reject(this.logger.logApiError(err, "getPage"))
        });
      });
      return promise;
    }
  }

  initClientCache(doc, token, grayScale, pageNumber) {
    this.apiV1.initCache(doc, token, grayScale).then(
      data => {
        return data['pages'];
      }
    ).then(
      pages => {
        if (pages === undefined) return;
        var pagesToCache = this.pageCache.pagesToCache(pages, pageNumber);
        for (let p in pagesToCache) {
          if (!this.pageCache.contains(pagesToCache[p])) {
            this.getPage(doc, pagesToCache[p], token);
          }
        }
      }
    );
  }

  initCache(doc, pageNumber) {

    this.list(doc).then(
     data => {
        if (data === undefined || data['status'] !== 0) return undefined;
        return data['pages'];
      }
    ).then(
      pages => {
        if (pages === undefined) {
          return null;
        } else {
          var pagesToCache = this.pageCache.pagesToCache(pages, pageNumber);
          for (let p in pagesToCache) {
            if (!this.pageCache.contains(pagesToCache[p])) {
              this.getPage(doc, pagesToCache[p], undefined);
            }
          }
          return true;
        }
      }
    );
  }

  savePage(document, page, swrp, token, userId, pageContainsBackground, clientMode) {
    console.debug('Adding page to cache', page);
    this.pageCache.add(page, { page: swrp, userId: userId, status: 0 });
    this.pageCache.setDocumentModified(token !== undefined);

    var rawSwrp = {
      formatVersion: swrp.formatVersion,
      platform: swrp.platform,
      elements: swrp.elements
    };

    if (!pageContainsBackground) {
      rawSwrp.elements = rawSwrp.elements.filter(e => { return e.elementIdentificator !== 0; });
    }
    
    var promise = new Promise((resolve, reject) => {
      this.http.put("api/gallery/page?Document=" + document + "&Page=" + page, { Swrp: rawSwrp, Token: token, PageContainsBackground: pageContainsBackground, ClientMode: clientMode }).subscribe({
        next: (data: any) => resolve(data),
        error: (err: Error) => reject(this.logger.logApiError(err, "savePage"))
      });
    });
    return promise;
  }

  getPageMoves(document, page, reqPageForPageNo) {
    var url = "api/gallery/pagemoves?Document=" + document + "&Page=" + page;
    if (reqPageForPageNo) {
      url = url + "&ReturnPageMoveForPageNo=" + reqPageForPageNo;
    }
    console.debug('getPageMoves url', url);
    var promise = new Promise((resolve, reject) => {
        this.http.get(url).subscribe({
          next: (data: any) => {
            resolve(data);
          },
        error: (err: Error) => reject(this.logger.logApiError(err, "getPageMoves"))
      });
    });
    return promise;
  }

  edgeDetection(image, document, page) {
    var promise = new Promise((resolve, reject) => {
      this.http.post("api/page/edgedetection", { Image: image, Document: document, Page: page }).subscribe({
        next: (data: any) => resolve(data),
        error: (err: Error) => reject(this.logger.logApiError(err, "edgeDetection"))
      });
    });
    return promise;
  }

  applyImageFilter(image, document, page, filter, points, rgba) {
    if (points === undefined) points = [];
    var promise = new Promise((resolve, reject) => {
      this.http.post("api/page/imagefilter", { Image: image, Document: document, Page: page, Points: points, Filter: filter, Rgba: rgba }).subscribe({
        next: (data: any) => resolve(data),
        error: (err: Error) => reject(this.logger.logApiError(err, "applyImageFilter"))
      }); 
    });
    return promise;
  }

  doOAuthLogin(provider, params) {
    var url = 'api/vanauth/dologin';
    var promise = new Promise((resolve, reject) => {
      this.http.get(url + '?' + params).subscribe({
        next: (data: any) => resolve(data),
        error: (err: Error) => reject(this.logger.logApiError(err, "doOAuthLogin"))
      }); 
    });
    return promise;
  }

  doDropboxLoginAction(action, params) {
    var promise = new Promise((resolve, reject) => {
      this.http.get('api/dropbox/' + action + '/?' + params).subscribe({
        next: (data: any) => resolve(data),
        error: (err: Error) => reject(this.logger.logApiError(err, "doDropboxLoginAction"))
      });
    });
    return promise;
  }

  doGoogleDriveLoginAction(action, params) {
    var promise = new Promise((resolve, reject) => {
      this.http.get('api/googledrive/' + action + '/?' + params).subscribe({
        next: (data: any) => resolve(data),
        error: (err: Error) => reject(this.logger.logApiError(err, "doGoogleDriveLoginAction"))
      });
    });
    return promise;
  }

  doCloudBoxLoginAction(action, params) {
    var promise = new Promise((resolve, reject) => {
      this.http.get('api/cloudbox/' + action + '/?' + params).subscribe({
        next: (data: any) => resolve(data),
        error: (err: Error) => reject(this.logger.logApiError(err, "doCloudBoxLoginAction"))
      });
    });
    return promise;
  }

  setDocumentChangedSinceLastDownload(newValue) {
    this.documentChangedSinceLastDownload = newValue;
  }

  getDocumentChangedSinceLastDownload() {
    return this.documentChangedSinceLastDownload;
  }

  setUnsavedChangesTooltipClosedTime(newValue) {
    this.unsavedChangesTooltipClosedAt = newValue;
  }

  getUnsavedChangesTooltipClosedTime() {
    return this.unsavedChangesTooltipClosedAt;
  }
}
